/**
 * Provenance-aware template resolution — the single send gate for `{{variables}}`
 * (EMAIL_TEMPLATES_CANONICAL_SYSTEM). Moved out of
 * `apps/hub/lib/utils/crm/template-variables-resolver.ts` so `apps/hub` and
 * `apps/next-email-client` resolve templates identically; Hub re-exports it.
 */
import { extractTemplateVariables, mapContactToTemplateVariables, mapTicketToTemplateVariables, } from "./mappers.js";
import { reconcileManifestWithText, } from "./manifest.js";
/**
 * Replace template variables in text with actual values.
 *
 * Substitutes EXACTLY the keys present in `variables` (including a deliberate
 * empty string) — it does NOT decide which variables are allowed to be empty.
 * That policy lives in {@link renderTemplateCore}; this is the dumb substitution
 * step it drives. Kept exported because compose seeding pre-fills what it can and
 * intentionally leaves the rest as `{{token}}`.
 */
export function replaceTemplateVariables(text, variables, isHtml = true // Default to HTML, convert \n to <br>
) {
    let result = text;
    // Replace each variable found in the text
    Object.entries(variables).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            let processedValue = String(value);
            // First, convert literal "\n" strings (backslash + n) to actual newline characters
            processedValue = processedValue.replace(/\\n/g, "\n");
            // Then convert actual newline characters to line breaks based on format
            if (isHtml) {
                // For HTML emails, convert \n to <br> tags
                processedValue = processedValue.replace(/\n/g, "<br>");
            }
            // For plain text, \n stays as-is (no conversion needed)
            const regex = new RegExp(`\\{\\{\\s*${escapeRegExp(key)}\\s*\\}\\}`, "g");
            result = result.replace(regex, processedValue);
        }
    });
    return result;
}
/** Escape a placeholder token for safe use inside a RegExp source. */
function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
/**
 * Get unfilled template variables from text after substitution
 * Returns variables that couldn't be filled
 */
export function getUnfilledVariables(text) {
    return extractTemplateVariables(text);
}
/**
 * Check if text contains template variables
 */
export function hasTemplateVariables(text) {
    return /\{\{[^}]+\}\}/.test(text);
}
// ---------------------------------------------------------------------------
// Provenance-aware resolution (empty must be EXPLICIT)
// ---------------------------------------------------------------------------
/**
 * Thrown when a template cannot be sent because one or more variables have no
 * explicitly-resolved value. Carries the offending tokens so callers can surface
 * a helpful message or a structured error.
 */
export class UnfilledTemplateVariablesError extends Error {
    constructor(unfilledVariables) {
        super(`Cannot send email: unresolved template variables: ${unfilledVariables.join(", ")}. Please provide values for: {{${unfilledVariables.join("}}, {{")}}}.`);
        this.name = "UnfilledTemplateVariablesError";
        this.unfilledVariables = unfilledVariables;
    }
}
/** Auto-resolvable values from the contact/company/ticket (may contain blanks). */
function buildAutoVars(contact, ticket) {
    let vars = {};
    if (contact)
        vars = { ...vars, ...mapContactToTemplateVariables(contact) };
    if (ticket)
        vars = { ...vars, ...mapTicketToTemplateVariables(ticket) };
    return vars;
}
/**
 * Normalize sender-supplied values into a lookup keyed by every case/spacing
 * variant so `{{Email Signature}}` and `{{email_signature}}` both match. An
 * explicit empty string is preserved (it is a deliberate "send this blank").
 */
function normalizeOverrides(manualValues) {
    const map = new Map();
    if (!manualValues)
        return map;
    for (const [k, v] of Object.entries(manualValues)) {
        if (v === undefined || v === null)
            continue;
        const value = String(v);
        const keyTrim = k.trim();
        const keyLower = keyTrim.toLowerCase();
        const keyUnderscore = keyLower.replace(/\s+/g, "_");
        map.set(keyTrim, value);
        map.set(keyLower, value);
        map.set(keyUnderscore, value);
        // Signature alias works in both directions.
        if (keyUnderscore === "email_signature" ||
            keyUnderscore === "signature" ||
            keyLower === "email signature") {
            map.set("signature", value);
            map.set("email_signature", value);
            map.set("Email Signature", value);
        }
    }
    return map;
}
/** Explicit override for `token`, trying case/spacing variants. Undefined = none supplied. */
function lookupOverride(overrides, token) {
    const t = token.trim();
    if (overrides.has(t))
        return overrides.get(t);
    const lower = t.toLowerCase();
    if (overrides.has(lower))
        return overrides.get(lower);
    const underscore = lower.replace(/\s+/g, "_");
    if (overrides.has(underscore))
        return overrides.get(underscore);
    return undefined;
}
/** Non-blank auto value for `token`, or undefined. A blank auto value never counts. */
function lookupAuto(autoVars, token) {
    const t = token.trim();
    const direct = autoVars[t];
    if (typeof direct === "string" && direct.trim() !== "")
        return direct;
    const underscore = t.toLowerCase().replace(/\s+/g, "_");
    const alt = autoVars[underscore];
    if (typeof alt === "string" && alt.trim() !== "")
        return alt;
    return undefined;
}
/**
 * Resolve a template's variables and substitute them, deciding per variable
 * whether it is filled or UNFILLED. A variable is filled when, in priority order:
 *   1. the sender supplied an explicit override (including an explicit "");
 *   2. it is auto-sourced and the contact/ticket produced a NON-BLANK value;
 *   3. the manifest declares a non-null `defaultValue`;
 *   4. the manifest marks it `allowEmpty` (author's design-time "send blank").
 * Otherwise it is UNFILLED — crucially, an auto value that resolves BLANK does
 * NOT silently fill; it counts as unfilled unless (1)/(4) makes the empty explicit.
 *
 * A final residual `{{token}}` scan catches any placeholder not covered by the
 * manifest (typo / drift), so the invariant "no variable ever ships in a sent
 * email" holds regardless of manifest completeness.
 */
function renderTemplateCore(subject, body, options) {
    const autoVars = buildAutoVars(options.contact, options.ticket);
    const overrides = normalizeOverrides(options.manualValues);
    const manifest = reconcileManifestWithText(subject, body, options.manifest ?? null);
    const fill = {};
    const unfilled = [];
    for (const entry of manifest) {
        const token = entry.key;
        const override = lookupOverride(overrides, token);
        if (override !== undefined) {
            fill[token] = override; // explicit sender intent, empty allowed
            continue;
        }
        if (entry.source === "auto") {
            const auto = lookupAuto(autoVars, token);
            if (auto !== undefined) {
                fill[token] = auto;
                continue;
            }
        }
        if (entry.defaultValue !== null) {
            fill[token] = entry.defaultValue;
            continue;
        }
        if (entry.allowEmpty) {
            fill[token] = ""; // author declared blank intentional
            continue;
        }
        unfilled.push(token);
    }
    // Subject is plain text (don't convert \n); body is HTML (\n → <br>).
    const resolvedSubject = replaceTemplateVariables(subject, fill, false);
    const resolvedBody = replaceTemplateVariables(body, fill, true);
    const residual = [
        ...extractTemplateVariables(resolvedSubject),
        ...extractTemplateVariables(resolvedBody),
    ];
    const unfilledVariables = [...new Set([...unfilled, ...residual])];
    return { subject: resolvedSubject, body: resolvedBody, unfilledVariables };
}
/**
 * Resolve variables in subject/body with a unified source of truth. Non-throwing:
 * returns the resolved text plus any `unfilledVariables` so UI callers can prompt
 * for them. Send paths MUST additionally gate on `unfilledVariables.length === 0`
 * (or use {@link resolveTemplateStrict} / {@link assertNoUnfilledVariables}).
 */
export function resolveTemplate(subject, body, options = {}) {
    return renderTemplateCore(subject, body, options);
}
/**
 * Resolve and THROW {@link UnfilledTemplateVariablesError} if any variable is
 * unfilled. The single choke-point every template send should funnel through.
 */
export function resolveTemplateStrict(subject, body, options = {}) {
    const resolved = renderTemplateCore(subject, body, options);
    if (resolved.unfilledVariables.length > 0) {
        throw new UnfilledTemplateVariablesError(resolved.unfilledVariables);
    }
    return { subject: resolved.subject, body: resolved.body };
}
/**
 * Final send gate for ALREADY-RENDERED subject/body (e.g. the web compose server
 * route that receives text the client substituted). Pure text check — throws
 * {@link UnfilledTemplateVariablesError} if any `{{token}}` remains. This is the
 * server-side backstop that makes the "never send a raw variable" guarantee
 * impossible to bypass from the client.
 */
export function assertNoUnfilledVariables(subject, body) {
    const residual = [
        ...new Set([...extractTemplateVariables(subject), ...extractTemplateVariables(body)]),
    ];
    if (residual.length > 0) {
        throw new UnfilledTemplateVariablesError(residual);
    }
}
/**
 * Build a complete variable dictionary from available sources.
 *
 * Legacy merge (auto + manual, manual wins) with no provenance/empty policy —
 * retained for seeding/prefill callers that fill what they can and leave the
 * rest. NOT a send gate; send paths use {@link resolveTemplate} /
 * {@link resolveTemplateStrict}.
 */
export function buildTemplateVariables(contact, ticket, manualValues) {
    let vars = buildAutoVars(contact, ticket);
    const overrides = normalizeOverrides(manualValues);
    if (overrides.size > 0) {
        vars = { ...vars, ...Object.fromEntries(overrides) };
    }
    return vars;
}
/**
 * Preview template with variables filled — mirrors the real send resolution
 * (auto-blank counts as unfilled), so the preview shows exactly what would ship.
 */
export function previewTemplate(subject, body, contact, ticket) {
    return renderTemplateCore(subject, body, { contact, ticket });
}
